import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface ThemePreferences {
  theme_mode: 'light' | 'dark' | 'system' | 'schedule';
  dark_start_time: string;
  dark_end_time: string;
}

export function useThemeSchedule() {
  const { user } = useAuth();
  const { setTheme, theme } = useTheme();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['theme-preferences', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('user_theme_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as ThemePreferences | null;
    },
    enabled: !!user,
  });

  const updatePreferences = useMutation({
    mutationFn: async (newPreferences: Partial<ThemePreferences>) => {
      if (!user) throw new Error('User not authenticated');

      const { data: existing } = await supabase
        .from('user_theme_preferences')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_theme_preferences')
          .update(newPreferences)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_theme_preferences')
          .insert({ user_id: user.id, ...newPreferences });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme-preferences'] });
    },
  });

  const isTimeInRange = (startTime: string, endTime: string): boolean => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (startMinutes <= endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    } else {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }
  };

  useEffect(() => {
    if (!mounted || isLoading) return;

    const applyTheme = () => {
      const mode = preferences?.theme_mode || 'light';

      if (mode === 'light') {
        setTheme('light');
      } else if (mode === 'dark') {
        setTheme('dark');
      } else if (mode === 'system') {
        setTheme('system');
      } else if (mode === 'schedule') {
        const startTime = preferences?.dark_start_time || '18:00:00';
        const endTime = preferences?.dark_end_time || '06:00:00';
        const isDarkTime = isTimeInRange(startTime, endTime);
        setTheme(isDarkTime ? 'dark' : 'light');
      }
    };

    applyTheme();

    if (preferences?.theme_mode === 'schedule') {
      const interval = setInterval(applyTheme, 60000);
      return () => clearInterval(interval);
    }
  }, [preferences, mounted, isLoading, setTheme]);

  return {
    preferences,
    isLoading,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
    currentTheme: theme,
  };
}
